import { useState } from "react";
import { useLeadDetailModal } from "@/contexts/LeadDetailModalContext";
import { ModalDetalhesLeadCompleto } from "./ModalDetalhesLeadCompleto";
import { AbrirPedidoModal } from "@/components/pedido/AbrirPedidoModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { LeadCarteira } from "@/hooks/useLeadsCarteira";
import { toast } from "sonner";
import { buscarEValidarLeadParaPedido } from "@/lib/validarLeadParaPedido";
import { ModalJustificativaNegacao } from "./ModalJustificativaNegacao";
import { registrarLeadNegado } from "@/lib/leadHistoricoService";
import { useLigar } from '@/hooks/useLigar';

/**
 * Renderizador global do modal de detalhes do lead.
 * Deve ser colocado dentro do LeadDetailModalProvider no App.tsx
 * Permite abrir o modal de qualquer lugar da aplicação via useLeadDetailModal().open(leadId)
 */
export function LeadDetailModalRenderer() {
  const { isOpen, openedId, initialTab, close, isProviderReady } = useLeadDetailModal();
  const queryClient = useQueryClient();
  const ligar = useLigar();
  
  // States para controle do modal de pedido
  const [modalPedidoAberto, setModalPedidoAberto] = useState(false);
  const [leadParaPedido, setLeadParaPedido] = useState<LeadCarteira | null>(null);
  
  // States para modal de justificativa de negação
  const [modalNegacaoAberto, setModalNegacaoAberto] = useState(false);
  const [leadIdParaNegar, setLeadIdParaNegar] = useState<string | null>(null);
  const [negandoLead, setNegandoLead] = useState(false);

  // Buscar dados do lead quando modal abre
  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead-detail-modal', openedId],
    queryFn: async (): Promise<LeadCarteira | null> => {
      if (!openedId) return null;
      
      console.log('📡 [LeadDetailModalRenderer] Buscando lead:', openedId);
      
      // Buscar lead básico - inclui dados_master_lead
      const { data: leadData, error: leadError } = await supabase
        .from('leads_carteira')
        .select('*, dados_master_lead, master_lead_id, campanha_id, sub_tipo_portabilidade, origem_lead, em_modo_foco')
        .eq('id', openedId)
        .maybeSingle();
      
      if (leadError) {
        console.error('❌ [LeadDetailModalRenderer] Erro ao buscar lead:', leadError);
        toast.error('Não carreguei os dados do lead. Atualize a tela.');
        return null;
      }

      if (!leadData) {
        console.warn('⚠️ [LeadDetailModalRenderer] Lead não encontrado:', openedId);
        toast.error('Lead não encontrado');
        return null;
      }

      // Buscar nome do consultor se existir consultor_id
      let consultorNome: string | null = null;
      if (leadData.consultor_id) {
        const { data: consultorData } = await supabase
          .from('usuarios')
          .select('nome')
          .eq('id', leadData.consultor_id)
          .maybeSingle();
        consultorNome = consultorData?.nome || null;
      }

      // Buscar nome da equipe se existir equipe_id (cast para any pois tipo pode não estar atualizado)
      let equipeNome: string | null = null;
      const equipeId = (leadData as any).equipe_id;
      if (equipeId) {
        const { data: equipeData } = await supabase
          .from('equipes')
          .select('nome')
          .eq('id', equipeId)
          .maybeSingle();
        equipeNome = equipeData?.nome || null;
      }

      console.log('✅ [LeadDetailModalRenderer] Lead carregado:', leadData.razao_social);
      
      // Mapear para o tipo LeadCarteira com propriedades extras
      return {
        ...leadData,
        consultor_nome: consultorNome,
        equipe_nome: equipeNome,
      } as LeadCarteira;
    },
    enabled: isOpen && !!openedId,
    staleTime: 0,
  });

  // Mapear tipo_lead para origem do pedido
  const getOrigemLead = (tipoLead?: string | null): 'TT' | 'PORTABILIDADE' | 'RENOVACAO' | 'LEADS' => {
    if (tipoLead === 'TT_CARTEIRA') return 'TT';
    if (tipoLead === 'PORTABILIDADE_VIVO' || tipoLead === 'PORTABILIDADE_CPF') return 'PORTABILIDADE';
    if (tipoLead === 'RENOVACAO_CLARO') return 'RENOVACAO';
    return 'LEADS';
  };

  // Handler para sucesso na criação do pedido
  const handlePedidoSuccess = () => {
    setModalPedidoAberto(false);
    setLeadParaPedido(null);
    close();
    
    // Invalidar todas as queries relevantes
    queryClient.invalidateQueries({ queryKey: ['lead-detail-modal'] });
    queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
    queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
    queryClient.invalidateQueries({ queryKey: ['leads-prospeccao-hierarquia'] });
  };

  // Handler para mudança de status
  const handleStatusChange = async (leadId: string, status: 'NEGADO' | 'NEGOCIANDO' | 'FECHADO') => {
    if (status === 'NEGADO') {
      setLeadIdParaNegar(leadId);
      setModalNegacaoAberto(true);
      return;
    }
    
    // VALIDAÇÃO PARA FECHADO: usa função centralizada
    if (status === 'FECHADO' && lead) {
      // ⛔ Ler o lead do BANCO, não do cache desta tela (Douglas 02/09/2026).
      // O consultor salvava o Contato Real, via "Contato salvo!" e aqui levava
      // "Contato Real obrigatório" — porque este objeto vinha da query
      // ['lead-detail-modal'], que ficava montada e não era invalidada no save.
      // É a mesma régua que o AbrirPedidoModal já usa.
      const validacao = await buscarEValidarLeadParaPedido(lead.id);
      if (!validacao.valido) {
        toast.error(validacao.erro, {
          description: validacao.descricao,
          duration: 6000
        });
        return;
      }
      
      // ABRIR MODAL DE CRIAÇÃO DE PEDIDO (em vez de atualizar status diretamente)
      console.log('🚀 [LeadDetailModalRenderer] Abrindo modal de pedido para lead:', lead.id);
      setLeadParaPedido(lead);
      setModalPedidoAberto(true);
      return; // Não atualiza status aqui - o modal fará isso após criar o pedido
    }

    const { error } = await supabase
      .from('leads_carteira')
      .update({ 
        status_lead: status,
        data_ultima_interacao: new Date().toISOString()
      })
      .eq('id', leadId);

    if (error) {
      toast.error('Não foi possível avançar o status', { description: error.message, duration: 7000 });
      return;
    }

    toast.success(`Status alterado para ${status}`);
    queryClient.invalidateQueries({ queryKey: ['lead-detail-modal', leadId] });
    queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
    queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
  };

  // Handler para confirmar negação com motivo
  const handleConfirmarNegacao = async (motivo: string) => {
    if (!leadIdParaNegar) return;
    setNegandoLead(true);
    try {
      const { error } = await supabase
        .from('leads_carteira')
        .update({ 
          status_lead: 'NEGADO',
          motivo_negacao: motivo,
          data_ultima_interacao: new Date().toISOString()
        } as any)
        .eq('id', leadIdParaNegar);

      if (error) {
        toast.error('Não é possível negar este lead', { description: error.message, duration: 7000 });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        await registrarLeadNegado(leadIdParaNegar, user.id, motivo);
      }

      toast.success('Lead negado', { description: motivo });
      setModalNegacaoAberto(false);
      setLeadIdParaNegar(null);
      queryClient.invalidateQueries({ queryKey: ['lead-detail-modal', leadIdParaNegar] });
      queryClient.invalidateQueries({ queryKey: ['meus-leads-carteira'] });
      queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
      close();
    } finally {
      setNegandoLead(false);
    }
  };

  const handleLigar = (telefone: string) => {
    ligar(telefone);
  };

  // Não renderizar se provider não está pronto ou modal fechado
  if (!isProviderReady || !isOpen) return null;

  return (
    <>
      <ModalDetalhesLeadCompleto
        open={isOpen && !isLoading && !!lead}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        lead={lead || null}
        onStatusChange={handleStatusChange}
        onLigar={handleLigar}
        initialTab={initialTab || undefined}
      />
      
      {/* Modal de Criação de Pedido - Garante que pedido seja criado antes do status mudar */}
      <AbrirPedidoModal
        open={modalPedidoAberto}
        onOpenChange={(open) => {
          if (!open) {
            setModalPedidoAberto(false);
            setLeadParaPedido(null);
          }
        }}
        cnpjInicial={leadParaPedido?.cnpj}
        leadCompleto={leadParaPedido}
        whatsappInicial={leadParaPedido?.contato_real_telefone}
        origemLead={getOrigemLead(leadParaPedido?.tipo_lead)}
        onSuccess={handlePedidoSuccess}
      />

      {/* Modal de Justificativa de Negação */}
      <ModalJustificativaNegacao
        open={modalNegacaoAberto}
        onOpenChange={setModalNegacaoAberto}
        onConfirmar={handleConfirmarNegacao}
        leadId={leadIdParaNegar}
        loading={negandoLead}
      />
    </>
  );
}
